import React, { useCallback } from 'react'
import {
  Flex,
  Icon,
  Text,
  useColorModeValue,
  Image,
  useToast,
} from '@chakra-ui/react'
import { useDropzone } from 'react-dropzone'
import Card from 'components/card/Card'
import { MdUpload } from 'react-icons/md'

export default function Dropzone (props) {
  // Destructure preview from props
  const { onFileChange, title, preview, ...rest } = props
  const brandColor = useColorModeValue('brand.500', 'white')
  const textColorSecondary = 'secondaryGray.500'
  const boxBg = useColorModeValue('secondaryGray.300', 'navy.700')
  const toast = useToast()

  const onDrop = useCallback(
    acceptedFiles => {
      if (acceptedFiles.length > 0) {
        onFileChange(acceptedFiles[0])
      }
    },
    [onFileChange],
  )

  const onDropRejected = useCallback(
    fileRejections => {
      const error = fileRejections[0]?.errors[0]
      if (error?.code === 'file-too-large') {
        toast({
          title: 'File too large.',
          description: 'The image size cannot exceed 2MB.',
          status: 'error',
          duration: 4000,
          isClosable: true,
          position: 'top',
        })
      } else {
        toast({
          title: 'Invalid file.',
          description: error?.message || 'Please upload a valid image file.',
          status: 'error',
          duration: 4000,
          isClosable: true,
          position: 'top',
        })
      }
    },
    [toast]
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected,
    accept: { 'image/*': ['.jpeg', '.png', '.jpg', '.gif', '.svg', '.webp'] },
    multiple: false,
    maxSize: 2 * 1024 * 1024, // 2MB
  })

  // Set the container height and position for the Dropzone area
  const dropzoneStyles = {
    w: '100%',
    h: '200px', // Set a fixed height for the dropzone area
    border: '2px dashed',
    borderColor: isDragActive ? brandColor : textColorSecondary,
    borderRadius: '16px',
    cursor: 'pointer',
    p: '16px',
    transition: 'border .24s ease-in-out',
    bg: boxBg,
    position: 'relative', // IMPORTANT: Makes this the positioning context for the image
  }

  return (
    // Card is the outermost container
    <Card {...rest} mb='20px' align='center' p='0'>
      {' '}
      {/* Set p='0' to control padding inside the dropzone */}
      <Flex {...dropzoneStyles} {...getRootProps({ className: 'dropzone' })}>
        <input {...getInputProps()} />
        {preview ? (
          // 👇 When a preview exists, display the image and make it cover the container
          <Image
            src={preview}
            alt='Logo Preview'
            w='100%' // Fill the width of the Flex container
            h='100%' // Fill the height of the Flex container
            objectFit='contain'
            borderRadius='14px' // Match the Flex container's border radius
            zIndex='1'
          />
        ) : (
          // 👇 If no preview exists, show the default upload content, centered
          <Flex
            direction='column'
            align='center'
            justify='center'
            w='100%'
            h='100%'
          >
            <Icon as={MdUpload} w='80px' h='80px' color={brandColor} />
            <Flex justify='center' mx='auto' mb='12px'>
              <Text fontSize='xl' fontWeight='700' color={brandColor}>
                {title}
              </Text>
            </Flex>
            {isDragActive ? (
              <Text fontSize='sm' fontWeight='500' color={textColorSecondary}>
                Drop the files here...
              </Text>
            ) : (
              <Text fontSize='sm' fontWeight='500' color={textColorSecondary}>
                Drag 'n' drop or click to upload
              </Text>
            )}
            <Text
              fontSize='xs'
              fontWeight='500'
              color={textColorSecondary}
              mt={1}
            >
              PNG, JPG, GIF, SVG (max 10MB)
            </Text>
          </Flex>
        )}
      </Flex>
    </Card>
  )
}
